import { fraInMonths } from "./fra";

/**
 * Apply SSA's early-claim reduction or delayed-credit increase to a PIA,
 * producing the initial monthly benefit at a given claim age.
 *
 * Rules (per https://www.ssa.gov/oact/quickcalc/early_late.html):
 *   - Claiming early (before FRA):
 *       First 36 months early -> reduce by 5/9 of 1% per month
 *       Each additional month early beyond 36 -> reduce by 5/12 of 1% per month
 *   - Claiming late (after FRA, up to age 70):
 *       Each month delayed -> increase by 2/3 of 1% per month (8% per year)
 *       Birth year >= 1943 (covers entire TCA target audience)
 *   - Claim age clamped to [62, 70] for purposes of adjustment.
 */
export function applyClaimAdjustment(params: {
  pia: number;
  birthYear: number;
  claimAge: number;
}): { monthlyBenefit: number; pctOfPia: number } {
  const { pia, birthYear, claimAge } = params;
  const clampedClaimAge = Math.min(70, Math.max(62, claimAge));
  const claimAgeMonths = Math.round(clampedClaimAge * 12);
  const fraMonths = fraInMonths(birthYear);
  const delta = claimAgeMonths - fraMonths;

  let pct: number;
  if (delta < 0) {
    const monthsEarly = -delta;
    const first36 = Math.min(36, monthsEarly);
    const beyond36 = Math.max(0, monthsEarly - 36);
    const reduction = first36 * (5 / 9) * 0.01 + beyond36 * (5 / 12) * 0.01;
    pct = 1 - reduction;
  } else if (delta > 0) {
    const monthsLate = delta;
    const increase = monthsLate * (2 / 3) * 0.01;
    pct = 1 + increase;
  } else {
    pct = 1;
  }

  return {
    monthlyBenefit: pia * pct,
    pctOfPia: pct,
  };
}
